<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { twMerge } from "tailwind-merge";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";

import { postConfirmClaims } from "@/api";
import { useUser } from "@/composables/api/useUser";

import Button from "@/components/ui/Button.vue";
import Dialog from "@/components/ui/Dialog.vue";
import Spinner from "@/components/ui/Spinner.vue";

const emit = defineEmits<{
  (e: "continueClaim"): void;
  (e: "confirmClaims"): void;
}>();

const { data: user } = useUser();

const model = defineModel<boolean>({ required: true });

const { t } = useI18n();
const route = useRoute();
const queryClient = useQueryClient();

const isSubmitting = ref(false);

async function handleSubmit() {
  const receiptId = route.params.id;

  if (isSubmitting.value || typeof receiptId !== "string") {
    return;
  }

  isSubmitting.value = true;

  try {
    await postConfirmClaims(receiptId);

    queryClient.invalidateQueries({ queryKey: ["receipt", receiptId] });
    toast(t("toast.confirmClaimSuccess"), { position: "top-center" });
    emit("confirmClaims");
  } catch (error) {
    console.error(error);
  }

  isSubmitting.value = false;
}
</script>

<template>
  <Dialog v-model:open="model" hideCloseButton>
    <template #content>
      <div class="flex flex-col gap-12">
        <div class="flex flex-col gap-5">
          <h2
            :class="
              twMerge(
                'text-galbani text-2xl font-semibold',
                user?.current_mechanic === 'lactel' && 'text-lactel-blue',
              )
            "
          >
            {{ $t("dialog.confirmClaim.title") }}
          </h2>

          <p class="text-secondary text-base font-medium">
            {{ $t("dialog.confirmClaim.description") }}
          </p>
        </div>

        <div class="flex flex-col gap-5">
          <Button variant="secondary" :disabled="isSubmitting" @click="handleSubmit">
            <template v-if="!isSubmitting">{{ $t("dialog.confirmClaim.submit") }}</template>

            <Spinner
              v-else
              class="size-7"
              trackColor="rgba(255, 255, 255, 0.3)"
              color="rgba(255, 255, 255, 1)"
            />
          </Button>

          <Button variant="primary" @click="emit('continueClaim')">
            {{ $t("dialog.confirmClaim.another") }}
          </Button>
        </div>
      </div>
    </template>
  </Dialog>
</template>
